import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { X, Plus, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface JsonFieldsEditorProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
  ruleId: number;
}

interface FieldDefinition {
  key: string;
  value: any;
  type: 'string' | 'number' | 'boolean' | 'select';
  options?: string[]; // For select fields
}

// Predefined field configurations for common rule types
const FIELD_CONFIGURATIONS = {
  'cash-movement-threshold': [
    { key: 'type', type: 'string' as const, defaultValue: 'cash-movement-threshold', readonly: true },
    { key: 'thresholdAmount', type: 'number' as const, defaultValue: 1000, description: 'Minimum amount to trigger alert' },
    { key: 'currency', type: 'select' as const, defaultValue: 'USD', options: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'] },
    { key: 'lookbackDays', type: 'number' as const, defaultValue: 30, description: 'Number of days to look back' },
    { key: 'requireExplanation', type: 'boolean' as const, defaultValue: true, description: 'Require explanation for flagged movements' },
    { key: 'excludeSystemGenerated', type: 'boolean' as const, defaultValue: true, description: 'Exclude system-generated transactions' },
    { key: 'minimumTransactionCount', type: 'number' as const, defaultValue: 1, description: 'Minimum number of transactions to trigger' }
  ]
};

export const JsonFieldsEditor = React.memo(({ value, onChange, ruleId }: JsonFieldsEditorProps) => {
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [newFieldType, setNewFieldType] = useState<'string' | 'number' | 'boolean'>('string');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [ruleType, setRuleType] = useState<string>('');

  useEffect(() => {
    try {
      setJsonError(null);
      if (value) {
        const parsed = JSON.parse(value);
        if (typeof parsed === 'object' && parsed !== null) {
          // Detect rule type
          const detectedType = parsed.type || '';
          setRuleType(detectedType);
          
          // Convert parsed object to field definitions
          const fieldDefs: FieldDefinition[] = Object.entries(parsed).map(([key, val]) => ({
            key,
            value: val,
            type: typeof val === 'boolean' ? 'boolean' : 
                  typeof val === 'number' ? 'number' : 
                  key === 'currency' ? 'select' : 'string',
            options: key === 'currency' ? ['USD', 'EUR', 'GBP', 'CAD', 'AUD'] : undefined
          }));
          
          setFields(fieldDefs);
        } else {
          setFields([]);
        }
      } else {
        setFields([]);
      }
    } catch (error) {
      setJsonError('Invalid JSON format');
      setFields([]);
    }
  }, [value]);

  const updateFieldValue = useCallback((index: number, newValue: any) => {
    setFields(prev => {
      const updatedFields = [...prev];
      updatedFields[index] = { ...updatedFields[index], value: newValue };
      
      // Convert fields back to object and update parent
      const obj: any = {};
      updatedFields.forEach(field => {
        obj[field.key] = field.value;
      });
      
      onChange(JSON.stringify(obj, null, 2));
      return updatedFields;
    });
  }, [onChange]);

  const removeField = useCallback((index: number) => {
    setFields(prev => {
      const updatedFields = prev.filter((_, i) => i !== index);
      
      if (updatedFields.length === 0) {
        onChange('');
      } else {
        const obj: any = {};
        updatedFields.forEach(field => {
          obj[field.key] = field.value;
        });
        onChange(JSON.stringify(obj, null, 2));
      }
      
      return updatedFields;
    });
  }, [onChange]);

  const addField = useCallback(() => {
    if (newFieldKey.trim() && !fields.some(f => f.key === newFieldKey.trim())) {
      const convertedValue = newFieldType === 'number' ? 
        (isNaN(Number(newFieldValue)) ? 0 : Number(newFieldValue)) :
        newFieldType === 'boolean' ? 
        (newFieldValue.toLowerCase() === 'true') :
        newFieldValue;

      setFields(prev => {
        const updatedFields = [...prev, {
          key: newFieldKey.trim(),
          value: convertedValue,
          type: newFieldType,
          options: newFieldKey === 'currency' ? ['USD', 'EUR', 'GBP', 'CAD', 'AUD'] : undefined
        }];
        
        const obj: any = {};
        updatedFields.forEach(field => {
          obj[field.key] = field.value;
        });
        
        onChange(JSON.stringify(obj, null, 2));
        return updatedFields;
      });
      
      setNewFieldKey('');
      setNewFieldValue('');
      setNewFieldType('string');
    }
  }, [newFieldKey, newFieldValue, newFieldType, fields, onChange]);

  const initializeCashMovementTemplate = useCallback(() => {
    const template = FIELD_CONFIGURATIONS['cash-movement-threshold'];
    const fieldDefs: FieldDefinition[] = template.map(config => ({
      key: config.key,
      value: config.defaultValue,
      type: config.type,
      options: config.options
    }));
    
    setFields(fieldDefs);
    
    const obj: any = {};
    fieldDefs.forEach(field => {
      obj[field.key] = field.value;
    });
    
    onChange(JSON.stringify(obj, null, 2));
  }, [onChange]);

  const renderFieldInput = (field: FieldDefinition, index: number) => {
    const config = ruleType === 'cash-movement-threshold' ? 
      FIELD_CONFIGURATIONS['cash-movement-threshold'].find(c => c.key === field.key) : null;
    
    const isReadonly = config?.readonly || false;

    switch (field.type) {
      case 'boolean':
        return (
          <div className="flex items-center space-x-2 col-span-3">
            <Switch
              checked={field.value}
              onCheckedChange={(checked) => updateFieldValue(index, checked)}
              disabled={isReadonly}
            />
            <span className="text-sm text-muted-foreground">
              {field.value ? 'True' : 'False'}
            </span>
          </div>
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={field.value}
            onChange={(e) => updateFieldValue(index, Number(e.target.value) || 0)}
            placeholder="Enter number"
            className="col-span-3"
            disabled={isReadonly}
          />
        );
      
      case 'select':
        return (
          <Select
            value={field.value.toString()}
            onValueChange={(value) => updateFieldValue(index, value)}
            disabled={isReadonly}
          >
            <SelectTrigger className="col-span-3">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(option => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      default:
        return (
          <Input
            value={field.value}
            onChange={(e) => updateFieldValue(index, e.target.value)}
            placeholder="Enter value"
            className="col-span-3"
            disabled={isReadonly}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          Exception Rule Configuration
        </Label>
        {fields.length === 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={initializeCashMovementTemplate}
            className="text-xs"
          >
            Use Cash Movement Template
          </Button>
        )}
      </div>

      {jsonError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {jsonError}. Please check the JSON format.
          </AlertDescription>
        </Alert>
      )}

      {ruleType === 'cash-movement-threshold' && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <strong>Cash Movement Configuration:</strong> These settings control when cash movements 
            are flagged as unexplained and require documentation.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Existing Fields */}
      <div className="space-y-3">
        {fields.map((field, index) => {
          const config = ruleType === 'cash-movement-threshold' ? 
            FIELD_CONFIGURATIONS['cash-movement-threshold'].find(c => c.key === field.key) : null;
          
          return (
            <div key={field.key} className="grid grid-cols-5 gap-2 items-center">
              <div className="space-y-1">
                <Label className="text-sm font-medium">{field.key}:</Label>
                {config?.description && (
                  <p className="text-xs text-muted-foreground">{config.description}</p>
                )}
              </div>
              {renderFieldInput(field, index)}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeField(index)}
                className="flex items-center gap-1"
                disabled={config?.readonly}
              >
                <X className="h-3 w-3" />
                Remove
              </Button>
            </div>
          );
        })}
      </div>

      {/* Add New Field */}
      <div className="grid grid-cols-6 gap-2 items-end pt-2 border-t">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Field Name</Label>
          <Input
            value={newFieldKey}
            onChange={(e) => setNewFieldKey(e.target.value)}
            placeholder="e.g. threshold"
            className="text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Type</Label>
          <Select value={newFieldType} onValueChange={(value: 'string' | 'number' | 'boolean') => setNewFieldType(value)}>
            <SelectTrigger className="text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="string">Text</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="boolean">Boolean</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-xs text-muted-foreground">Field Value</Label>
          {newFieldType === 'boolean' ? (
            <Select value={newFieldValue} onValueChange={setNewFieldValue}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">True</SelectItem>
                <SelectItem value="false">False</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input
              type={newFieldType === 'number' ? 'number' : 'text'}
              value={newFieldValue}
              onChange={(e) => setNewFieldValue(e.target.value)}
              placeholder="Enter value"
              className="text-sm"
            />
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addField}
          disabled={!newFieldKey.trim() || fields.some(f => f.key === newFieldKey.trim())}
          className="flex items-center gap-1 col-span-2"
        >
          <Plus className="h-3 w-3" />
          Add Field
        </Button>
      </div>

      {fields.length === 0 && (
        <div className="text-sm text-muted-foreground italic text-center py-4 border border-dashed rounded-md">
          No exception rules configured. Add fields above to define exception conditions or use a template.
        </div>
      )}
    </div>
  );
});

JsonFieldsEditor.displayName = 'JsonFieldsEditor'; 